import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'

interface RecaptchaClient {
    ready: (callback: () => void) => void
    execute: (siteKey: string, options: { action: string }) => Promise<string>
}

declare global {
    interface Window {
        grecaptcha?: RecaptchaClient & { enterprise?: RecaptchaClient }
    }
}

@Injectable({
    providedIn: 'root',
})
export class RecaptchaService {
    private scriptLoaded?: Promise<void>

    execute(action: string): Promise<string> {
        if (environment.useEmulators) {
            return Promise.resolve('emulator')
        }

        if (!environment.recaptchaSiteKey) {
            if (environment.production) {
                return Promise.reject(new Error('Bot protection is not configured'))
            }
            return Promise.resolve('emulator')
        }

        return this.loadScript().then(() => this.executeAction(action))
    }

    private recaptchaClient(): RecaptchaClient | undefined {
        return window.grecaptcha?.enterprise || window.grecaptcha
    }

    private executeAction(action: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const client = this.recaptchaClient()
            if (!client) {
                reject(new Error('reCAPTCHA failed to load'))
                return
            }

            client.ready(() => {
                client.execute(environment.recaptchaSiteKey, { action }).then(resolve).catch(reject)
            })
        })
    }

    private loadScript(): Promise<void> {
        if (this.recaptchaClient()) {
            return Promise.resolve()
        }
        if (this.scriptLoaded) {
            return this.scriptLoaded
        }

        this.scriptLoaded = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script')
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.recaptchaSiteKey}`
            script.async = true
            script.defer = true
            script.onload = () => resolve()
            script.onerror = () => reject(new Error('Failed to load reCAPTCHA'))
            document.head.appendChild(script)
        })

        return this.scriptLoaded
    }
}
